import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Send, 
  Bot, 
  User, 
  AlertTriangle,
  Loader2,
  RefreshCw,
  Paperclip,
  FileText,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const suggestedQuestions = [
  "Qu'est-ce qu'une mise en demeure ?",
  "Quels sont mes droits face à un contrôle ?",
  "Comment prouver ma bonne foi ?",
  "Dois-je répondre aux questions de la gendarmerie ?",
  "Qu'est-ce que la loi ALUR dit sur l'habitat léger ?"
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      const allowedExtensions = ['pdf', 'docx', 'txt'];
      const extension = file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
        toast.error('Format non supporté. Utilisez PDF, DOCX ou TXT.');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 5 Mo)');
        return;
      }
      
      setSelectedFile(file);
      toast.success(`Fichier "${file.name}" sélectionné`);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = { 
      role: 'user', 
      content: text,
      fileName: selectedFile?.name 
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      let response;
      
      if (selectedFile) {
        // Use multipart form data for file upload
        const formData = new FormData();
        formData.append('message', text);
        formData.append('file', selectedFile);
        if (sessionId) {
          formData.append('session_id', sessionId);
        }
        
        response = await axios.post(`${API}/chat/with-file`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Clear file after successful upload
        removeFile();
      } else {
        // Standard text-only request
        response = await axios.post(`${API}/chat`, {
          message: text,
          session_id: sessionId
        });
      }
      
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      const aiMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la communication avec l\'assistant';
      toast.error(errorMsg);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Désolé, une erreur s\'est produite. Veuillez réessayer.',
        isError: true 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSuggestion = (question) => {
    sendMessage(question);
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Nouvelle conversation démarrée');
  };

  return (
    <div className="min-h-screen" data-testid="assistant-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Assistant juridique IA
          </h1>
          <p className="text-muted-foreground">
            Posez vos questions et obtenez des réponses pour vous orienter dans vos démarches.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Disclaimer */}
        <Card className="mb-6 border-orange-200 bg-orange-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-orange-800 mb-1">Information importante</p>
                <p className="text-sm text-orange-700">
                  Cet assistant fournit des informations générales à titre indicatif. 
                  Il ne remplace pas l'avis d'un avocat. Pour les situations complexes, 
                  consultez un professionnel du droit.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chat Container */}
        <Card className="h-[600px] flex flex-col">
          <CardHeader className="pb-3 border-b flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              Assistant Habitat Léger
            </CardTitle>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={resetChat} data-testid="reset-chat-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Nouvelle conversation
              </Button>
            )}
          </CardHeader>

          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Bot className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Bienvenue !</h3>
                  <p className="text-muted-foreground mb-6 max-w-md">
                    Je suis là pour vous aider à comprendre vos droits et vous orienter 
                    dans vos démarches liées à l'habitat léger.
                  </p>
                  <div className="w-full max-w-md">
                    <p className="text-sm font-medium mb-3">Questions fréquentes :</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {suggestedQuestions.map((q, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => handleSuggestion(q)}
                          data-testid={`suggestion-${i}`}
                        >
                          {q}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${index}`}
                    >
                      <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          msg.role === 'user' ? 'bg-primary' : 'bg-muted'
                        }`}>
                          {msg.role === 'user' ? (
                            <User className="w-4 h-4 text-primary-foreground" />
                          ) : (
                            <Bot className="w-4 h-4 text-foreground" />
                          )}
                        </div>
                        <div className={`px-4 py-3 rounded-lg ${
                          msg.role === 'user' 
                            ? 'bg-primary text-primary-foreground rounded-br-none' 
                            : msg.isError 
                              ? 'bg-destructive/10 text-destructive rounded-bl-none'
                              : 'bg-muted rounded-bl-none'
                        }`}>
                          {msg.fileName && (
                            <div className="flex items-center gap-1 text-xs opacity-80 mb-1">
                              <FileText className="w-3 h-3" />
                              {msg.fileName}
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <Bot className="w-4 h-4" />
                        </div>
                        <div className="px-4 py-3 bg-muted rounded-lg rounded-bl-none">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t">
              {/* File preview */}
              {selectedFile && (
                <div className="mb-3 flex items-center gap-2 p-2 bg-muted rounded-lg">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="text-sm flex-1 truncate">{selectedFile.name}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={removeFile}
                    className="h-6 w-6 p-0"
                    data-testid="remove-file-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.docx,.txt"
                  className="hidden"
                  data-testid="file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  title="Joindre un document (PDF, DOCX, TXT)"
                  data-testid="attach-file-btn"
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={selectedFile ? "Posez votre question sur ce document..." : "Posez votre question..."}
                  disabled={loading}
                  className="flex-1"
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={loading || !input.trim()} data-testid="send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Vous pouvez joindre un document (PDF, DOCX, TXT) pour que l'assistant l'analyse
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Additional help */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="py-4">
              <h4 className="font-medium mb-2">Besoin de plus d'aide ?</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Consultez notre documentation complète ou trouvez un avocat spécialisé.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild>
                  <a href="/documentation">Documentation</a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="/avocats">Trouver un avocat</a>
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <h4 className="font-medium mb-2">Situation urgente ?</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Référencez votre situation sur la carte pour bénéficier du soutien de la communauté.
              </p>
              <Button variant="outline" size="sm" asChild>
                <a href="/carte">Voir la carte</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
